import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { IntlShape, useIntl } from "react-intl";
import { QrReader } from "react-qr-reader";
import {
	Box,
	Button,
	IconButton,
	Typography,
	Avatar,
	Stack,
	Dialog,
	DialogContent,
	DialogTitle,
	useMediaQuery,
	lighten,
	darken,
	useTheme,
} from "@mui/material";

import { useAuthContext } from "context/GameAuthContext";

import { isFeatureEnabled } from "feature-configs/featureConfigUtils";
import { FeatureConfig } from "feature-configs/featureConfigApi";

import { ReactComponent as MailIcon } from "assets/svg/mail.svg";
import { ReactComponent as BellIcon } from "assets/svg/bell.svg";
import { ReactComponent as LogoutIcon } from "assets/svg/logout.svg";
import { ReactComponent as ArrowLeft } from "assets/svg/arrow-left.svg";
import { ReactComponent as QRPinIcon } from "assets/svg/qr-pin.svg";
import { ReactComponent as QRReaderIcon } from "assets/svg/qr-reader.svg";

import { ConfirmDialog } from "components/dialog/ConfirmDialog";
import Preloader from "components/PreLoader";
import { ButtonContained } from "components/button/ButtonContained";
import { PINDialog } from "components/dialog/PINDialog";
import { TestModeButton } from "components/TestModeButton";
import { ButtonOutlined } from "components/button/ButtonOutlined";

import { UserLevelContext } from "utils/contexts/user-level";
import { UserDailyMissionContext } from "utils/contexts/user-daily-mission";
import { BuddyDetailsContext } from "utils/contexts/buddy-details";
import { getInvertColor } from "utils/themeUtils";

import { localStorageShowConfirmNotifyBuddy } from "auth/helpers/authApi";

import UserJourneyProgress from "./UserJourneyProgress";
import SelectDashboard from "./SelectDashboard";
import { LevelDialog } from "../LevelDialog";

export interface HeaderProps {
	username: string;
	companyLogo: string;
}
type TitleProps = Pick<HeaderProps, "username"> & { intl: IntlShape };
type UpperSectionProps = Pick<HeaderProps, "companyLogo"> & { intl: IntlShape };
interface MiddleSectionProps extends HeaderProps {
	intl: IntlShape;
}

const Header = ({ username, companyLogo }: HeaderProps) => {
	const intl = useIntl();

	return (
		<Box
			display="flex"
			flexDirection="column"
			justifyContent="space-between"
			p="3.2rem"
			pt="2.4rem"
			height="29.32rem"
			sx={{
				background: (theme) =>
					getInvertColor(theme.palette.primary.main) === "#fff"
						? `transparent linear-gradient(162deg, ${lighten(theme.palette.primary.main, 0.3)}, ${
								theme.palette.primary.main
						  } 75%) 0% 0% no-repeat padding-box`
						: `transparent linear-gradient(162deg, ${lighten(theme.palette.primary.main, 0.8)} 35%, ${
								theme.palette.primary.main
						  }) 0% 0% no-repeat padding-box`,
			}}
			className="user-dashboard-header-container"
		>
			<UpperSection intl={intl} companyLogo={companyLogo} />
			<MiddleSection username={username} intl={intl} companyLogo={companyLogo} />
		</Box>
	);
};

const Title = ({ intl, username }: TitleProps) => {
	const { userLevel } = useContext(UserLevelContext);

	return (
		<Stack
			flexDirection="row"
			alignItems="center"
			columnGap="1.6rem"
			flexWrap="wrap"
			className="user-dashboard-title-wrapper"
		>
			<Typography
				data-testid="user_dashboard_header_welcome"
				width="fit-content"
				fontWeight={800}
				fontSize="3.8rem"
				color={(theme) => getInvertColor(theme.palette.primary.main)}
				className="user-dashboard-title"
			>
				{intl.formatMessage({ id: "USERDASHBOARD.header.welcome" })} {username}
			</Typography>
			{userLevel && (
				<Typography
					component="span"
					fontWeight={800}
					fontSize="2.2rem"
					color="#FCB105"
					className="user-dashboard-level"
					sx={{ transform: "translateY(0.5rem)" }}
				>
					{intl.formatMessage({ id: "COMMON.level" })}: {userLevel.currentLevel}
				</Typography>
			)}
		</Stack>
	);
};

const UpperSection = ({ intl, companyLogo }: UpperSectionProps) => {
	const { logout, user } = useAuthContext();

	return (
		<Stack flexDirection="row" justifyContent="space-between">
			<img id="logo-company" src={companyLogo} alt="logo" style={{ height: "3.6rem" }} />
			<Stack flexDirection={"row"} gap={2}>
				{user?.isBuddy && isFeatureEnabled(FeatureConfig.buddy) && <SelectDashboard />}
				{user?.isTestMode ? (
					<TestModeButton onClick={() => window.close()} />
				) : (
					<ButtonOutlined
						id="button-logout"
						data-useremunit="true"
						sx={{
							fontSize: "1.6rem",
							color: (theme) => getInvertColor(theme.palette.primary.main),
							borderColor: (theme) => getInvertColor(theme.palette.primary.main),

							"&:hover": {
								color: "common.black",
								bgcolor: "common.white",
								borderColor: "common.white",
							},
							"&:hover svg path": {
								fill: "#000",
							},
							"& svg path": {
								fill: (theme) => getInvertColor(theme.palette.primary.main),
							},
						}}
						className="user-dashboard-logout-button"
						onClick={logout}
					>
						<LogoutIcon width="2rem" className="logout-icon" />
						{intl.formatMessage({ id: "USERDASHBOARD.header.logout" })}
					</ButtonOutlined>
				)}
			</Stack>
		</Stack>
	);
};

const dailyMissionPINSchema = z.object({
	pin: z.string(),
});

export type DailyMissionPIN = z.infer<typeof dailyMissionPINSchema>;

const MiddleSection = ({ intl, username, companyLogo }: MiddleSectionProps) => {
	const theme = useTheme();
	const matchesMobile = useMediaQuery("(max-width:34em)");

	const { openLevel, userLevel, triggerHideLevel } = useContext(UserLevelContext);
	const {
		userDailyMission,
		loading: missionLoading,
		completeMission,
		openPIN,
		setOpenPIN,
		openReader,
		setOpenReader,
	} = useContext(UserDailyMissionContext);
	const {
		details,
		detailsLoading,
		notifyLoading,
		openDetails,
		setOpenDetails,
		openConfirm,
		setOpenConfirm,
		notifyBuddy,
	} = useContext(BuddyDetailsContext);

	const { control, handleSubmit } = useForm<DailyMissionPIN>();

	const scanHandler = (result) => {
		if (!!result) {
			completeMission({ pin: result.text });
		}
	};

	const isLight = getInvertColor(theme.palette.primary.main) === "#3C4255";

	return (
		<Box
			display="flex"
			alignItems="flex-end"
			justifyContent="space-between"
			gap="3.2rem"
			margin="0 auto"
			width="90%"
			className="middle-section-container"
		>
			<Dialog
				open={openReader}
				PaperProps={{
					sx: {
						position: "relative",
						m: 0,
						maxWidth: "none",
						maxHeight: "none",
						width: "100%",
						height: "100svh",
						bgcolor: "grey.700",
						color: "common.white",
						borderRadius: 0,
					},
				}}
			>
				<DialogTitle
					sx={{
						position: "relative",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						p: "24px",
					}}
				>
					<IconButton
						sx={{
							position: "absolute",
							top: "50%",
							left: "24px",
							transform: "translateY(-50%)",
							width: "32px",
							height: "32px",
							"& svg path": { fill: "#FFF" },
							zIndex: 9999,
						}}
						onClick={() => setOpenReader(false)}
					>
						<ArrowLeft />
					</IconButton>
					<Typography component="span" fontWeight={700} fontSize="18px" color="common.white">
						{intl.formatMessage({ id: "DASHBOARD.employee-journey.daily-mission.scan-qr-mobile-title" })}
					</Typography>
				</DialogTitle>
				<QrReader
					constraints={{
						facingMode: "environment",
						aspectRatio: 1,
					}}
					onResult={scanHandler}
					className="user-dashboard-qr-reader"
				/>
				<ButtonContained
					sx={{
						mx: "auto",
						mt: "32px",
						width: "150px",
						height: "45px",
						fontSize: "16px",
					}}
					onClick={() => {
						setOpenPIN(true);
						setOpenReader(false);
					}}
				>
					{intl.formatMessage({ id: "DASHBOARD.employee-journey.daily-mission.input-pin" })}
				</ButtonContained>
			</Dialog>
			<PINDialog
				useRemUnit
				open={openPIN}
				onClose={() => setOpenPIN(false)}
				onCancel={() => setOpenPIN(false)}
				title={intl.formatMessage({ id: "DASHBOARD.employee-journey.daily-mission.input-pin-description" })}
				controllerProps={{ control, name: "pin" }}
				otpFieldsProps={{
					onFilled: handleSubmit(completeMission),
				}}
			/>

			<ConfirmDialog
				useRemUnit
				open={openConfirm}
				onClose={() => setOpenConfirm(false)}
				title={intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy.confirm" }, { name: username })}
				logo={companyLogo}
				buttonOutlinedProps={{ disabled: notifyLoading, onClick: () => setOpenConfirm(false) }}
				buttonContainedProps={{ disabled: notifyLoading, loading: notifyLoading, onClick: notifyBuddy }}
				PaperProps={{
					className: "user-dashboard-confirm-notify-dialog",
				}}
			/>
			{/* ----- LEVEL DIALOG ----- */}
			{openLevel && (
				<LevelDialog
					companyLogo={companyLogo}
					open={openLevel}
					matchesMobile={matchesMobile}
					userLevel={userLevel}
					triggerHideLevel={triggerHideLevel}
				/>
			)}
			{/* ----- BUDDY DETAILS DIALOG MOBILE ----- */}
			<Dialog
				open={openDetails}
				PaperProps={{
					sx: {
						m: 0,
						maxWidth: "none",
						maxHeight: "none",
						width: "100%",
						height: "100svh",
						borderRadius: 0,
					},
				}}
			>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: "96px" }}>
					<Stack flexDirection="row" alignItems="center" gap="16px" pt="32px">
						<IconButton sx={{ width: "32px", height: "32px" }} onClick={() => setOpenDetails(false)}>
							<ArrowLeft />
						</IconButton>
						<Typography component="span" fontWeight={700} fontSize="18px" color="grey.600">
							{intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy.my-buddy" })}
						</Typography>
					</Stack>
					<Stack alignItems="center" justifyContent="space-between" flex="1" gap="32px" pb="96px">
						<Stack
							flexDirection="row"
							alignItems="center"
							justifyContent="center"
							gap="32px"
							mx="auto"
							width="100%"
						>
							<Avatar
								src={details?.profilePicture || ""}
								alt={`${details?.name}'s avatar.`}
								sx={{ width: "96px", height: "96px", bgcolor: "legacyPrimary.200" }}
							></Avatar>
							<Stack>
								<Stack gap="8px">
									<Typography fontWeight={600} fontSize="16px" color="grey.700" mb="4px">
										{details?.name?.split(" ")[0]}
									</Typography>
									<Stack flexDirection="row" gap="8px">
										<MailIcon />
										<Typography
											component="span"
											fontSize="14px"
											color="common.black"
											sx={{ wordBreak: "break-all", transform: "translateY(-5px)" }}
										>
											{details?.email}
										</Typography>
									</Stack>
								</Stack>
							</Stack>
						</Stack>
						<ButtonContained
							loading={notifyLoading}
							startIcon={<BellIcon width="16" height="18" />}
							onClick={
								localStorage.getItem(localStorageShowConfirmNotifyBuddy) === "hide"
									? notifyBuddy
									: () => setOpenConfirm(true)
							}
						>
							{intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy" })}
						</ButtonContained>
					</Stack>
				</DialogContent>
			</Dialog>
			<Stack gap="2.4rem" width="fit-content" className="middle-section-progress-container">
				<Stack flexDirection="row" alignItems="center" justifyContent="space-between">
					<Title intl={intl} username={username} />
					{!!details && (
						<Button
							disableElevation
							variant="contained"
							onClick={() => setOpenDetails(true)}
							sx={{
								py: 0,
								height: "36px",
								bgcolor: "#5993F4",
								borderRadius: "8px",

								":hover": {
									bgcolor: "#5084dc",
								},
							}}
							className="user-dashboard-my-buddy-button"
						>
							<Typography component="span" fontWeight={600} fontSize="13px" color="common.white">
								{intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy.my-buddy" })}
							</Typography>
						</Button>
					)}
				</Stack>
				<UserJourneyProgress />
			</Stack>
			<Stack flexDirection="row" gap="1.6rem" className="user-dashboard-cards-container">
				{isFeatureEnabled(FeatureConfig.buddy) && (detailsLoading || !!details) && (
					<Stack
						position="relative"
						flexDirection="row"
						alignItems="center"
						flex="1"
						gap="12px"
						p="12px 2.4rem"
						borderRadius="10px"
						width="32rem"
						height="132px"
						sx={{ bgcolor: "common.white" }}
						className={`user-dashboard-notify-card ${
							!!userDailyMission && userDailyMission?.isCompleted ? "no-mission" : ""
						}`}
					>
						{detailsLoading ? (
							<Preloader spinnerProps={{ size: "32px" }} />
						) : (
							<>
								<Avatar
									src={details?.profilePicture || ""}
									alt={`${details?.name}'s avatar.`}
									sx={{ width: "84px", height: "84px", bgcolor: "legacyPrimary.200" }}
								></Avatar>
								<Stack
									title={`${intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy.your-buddy" })}: ${
										details?.name
									}`}
									flex="1"
									py="12px"
									pl="8px"
									overflow="hidden"
								>
									<Typography
										whiteSpace="nowrap"
										overflow="hidden"
										textOverflow="ellipsis"
										fontWeight={600}
										fontSize="14px"
										color="grey.700"
									>
										{intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy.your-buddy" })}:{" "}
										<Typography
											component="span"
											fontWeight={700}
											fontSize="14px"
											color={(theme) =>
												isLight ? darken(theme.palette.primary.main, 0.5) : "primary.main"
											}
										>
											{details?.name?.split(" ")[0]}
										</Typography>
									</Typography>
									<Stack
										title={details?.email}
										flexDirection="row"
										alignItems="center"
										gap="8px"
										mt="4px"
										mb="8px"
									>
										<MailIcon />
										<Typography
											id="text-buddy-email"
											component="span"
											fontSize="13px"
											color="common.black"
											whiteSpace="nowrap"
											overflow="hidden"
											textOverflow="ellipsis"
										>
											{details?.email}
										</Typography>
									</Stack>
									<ButtonContained
										id="button-notify-buddy"
										data-useremunit="true"
										startIcon={<BellIcon width="1.3rem" />}
										loading={notifyLoading}
										onClick={
											localStorage.getItem(localStorageShowConfirmNotifyBuddy) === "hide"
												? notifyBuddy
												: () => setOpenConfirm(true)
										}
										sx={{
											fontWeight: 700,
											fontSize: "13px",
											letterSpacing: "0.25px",
										}}
									>
										{intl.formatMessage({ id: "USER-DASHBOARD.notify-buddy" })}
									</ButtonContained>
								</Stack>
							</>
						)}
					</Stack>
				)}

				{(missionLoading || (!!userDailyMission && !userDailyMission?.isCompleted)) && (
					<Stack
						id="card-daily-mission"
						position="relative"
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						flex="1"
						gap="12px"
						p="12px 2.4rem"
						borderRadius="10px"
						width="36rem"
						height="132px"
						sx={{ bgcolor: "common.white" }}
						className="user-dashboard-mission-card"
					>
						{missionLoading ? (
							<Preloader spinnerProps={{ size: "32px" }} />
						) : (
							<>
								<Stack flexBasis="75%" gap="8px" overflow="hidden">
									<Typography fontWeight={600} fontSize="13px" color="grey.700">
										{intl.formatMessage({
											id: "DASHBOARD.employee-journey.daily-mission.your-daily-mission",
										})}
										:
									</Typography>
									<Typography
										title={userDailyMission?.dailyMissionId?.title}
										fontWeight={800}
										fontSize="16px"
										color={(theme) =>
											isLight ? darken(theme.palette.primary.main, 0.5) : "primary.main"
										}
										whiteSpace="nowrap"
										overflow="hidden"
										textOverflow="ellipsis"
									>
										{userDailyMission?.dailyMissionId?.title}
									</Typography>
									<Typography
										title={userDailyMission?.dailyMissionId?.description}
										fontSize="13px"
										color="grey.700"
										sx={{
											display: "-webkit-box",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textOverflow: "ellipsis",
											maxHeight: "36px",
										}}
									>
										{userDailyMission?.dailyMissionId?.description}
									</Typography>
								</Stack>
								<IconButton
									onClick={() => {
										if (matchesMobile) {
											setOpenReader(true);
										} else {
											setOpenPIN(true);
										}
									}}
								>
									{matchesMobile ? <QRReaderIcon width="5.6rem" /> : <QRPinIcon width="5.6rem" />}
								</IconButton>
							</>
						)}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};

export default Header;
